import React, {
  createContext,
  useContext,
  ReactNode,
  useState,
  useCallback,
  useMemo,
} from 'react'
import {
  TabGroups,
  createTabGroups,
  DataTabCounts,
} from '../components/SideMenu'
import apiService from 'services/api'

export type DataTabKey =
  | 'filters'
  | 'productTags'
  | 'contactTags'
  | 'documentTags'
  | 'columnPresets'

interface SideMenuContextType {
  tabGroups: TabGroups
  logoCount: number
  isLoading: boolean
  setDataTabCount: (key: DataTabKey, count: number) => void
}

const SideMenuContext = createContext<SideMenuContextType | undefined>(
  undefined
)

export function SideMenuProvider({ children }: { children: ReactNode }) {
  const api = apiService()
  const { data: logos, isLoading } = api.useGetCompanyLogos()

  const [dataTabCounts, setDataTabCounts] = useState<DataTabCounts>({
    filters: null,
    productTags: null,
    contactTags: null,
    documentTags: null,
    columnPresets: null,
  })

  const setDataTabCount = useCallback((key: DataTabKey, count: number) => {
    setDataTabCounts((prev) => ({ ...prev, [key]: count }))
  }, [])

  const logoCount = logos?.length || 0
  const tabGroups = useMemo(
    () => createTabGroups(logoCount, dataTabCounts),
    [logoCount, dataTabCounts]
  )

  return (
    <SideMenuContext.Provider
      value={{
        tabGroups,
        logoCount,
        isLoading,
        setDataTabCount,
      }}
    >
      {children}
    </SideMenuContext.Provider>
  )
}

export function useSideMenu(): SideMenuContextType {
  const context = useContext(SideMenuContext)
  if (context === undefined) {
    throw new Error('useSideMenu must be used within a SideMenuProvider')
  }
  return context
}
